class DrawerFreeShippingBar extends HTMLElement {
  constructor() {
    super();
    
    // Initialize properties
    this.isLoading = true;
    this.cartTotal = 0;
    this.currentCartTotal = 0;
    this.threshold = 0;
    this.currency = '$';
    this.sectionId = '';
    this.isInitialized = false;
    this.discountRules = [];
    this.freeShippingRules = [];
    this.discountOnlyRules = [];
    
    // Store element references
    this.progressBarFill = null;
    this.milestoneElements = {
      combined: null,
      freeShipping: null,
      discount: null,
      default: null
    };
    this.amountLeftElement = null;
    this.discountAmountElement = null;
    this.loadingIndicator = null;
    this.container = null;
  }
  
  static get observedAttributes() {
    return ['data-cart-total', 'data-currency', 'data-section-id', 'data-discount'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'data-cart-total':
        this.cartTotal = parseFloat(newValue) || 0;
        if (this.isInitialized) this.updateProgressBar(this.cartTotal);
        break;
      case 'data-currency':
        this.currency = newValue || '$';
        if (this.isInitialized) this.updateProgressBar(this.cartTotal);
        break;
      case 'data-section-id':
        this.sectionId = newValue || '';
        break;
      case 'data-discount':
        this.parseDiscountData(newValue);
        if (this.isInitialized) this.updateProgressBar(this.cartTotal);
        break;
    }
  }
  
  // Parse discount data from the JSON
  parseDiscountData(discountJson) {
    try {
      if (!discountJson) {
        console.warn('No discount data provided');
        return;
      }
      
      let discountData;
      
      // Check if the string is already an object (happens sometimes with Liquid)
      if (typeof discountJson === 'object') {
        discountData = discountJson;
      } else {
        // Try to sanitize the JSON string before parsing
        let cleanJson = discountJson;
        
        // Check if it's a HTML-escaped JSON string
        if (typeof cleanJson === 'string') {
          // Replace HTML entities and fix common rendering issues
          cleanJson = cleanJson.replace(/&quot;/g, '"')
                                .replace(/&#39;/g, "'")
                                .replace(/&lt;/g, '<')
                                .replace(/&gt;/g, '>')
                                .replace(/&amp;/g, '&')
                                .replace(/\=\"\{/g, '={')
                                .replace(/\}\"/g, '}')
                                .replace(/\"\{/g, '{')
                                .replace(/\}\"/g, '}');
          
          // Try to extract JSON from attribute
          if (cleanJson.indexOf('data-discount=') >= 0 || cleanJson.indexOf(' discount_rules') >= 0) {
            const attrMatch = cleanJson.match(/data-discount="([^"]*)"/);
            if (attrMatch && attrMatch[1]) {
              cleanJson = attrMatch[1];
            } else {
              const jsonMatch = cleanJson.match(/(\{.*\})/);
              if (jsonMatch) cleanJson = jsonMatch[0];
            }
          }
          
          // Try various parsing approaches
          try {
            discountData = JSON.parse(cleanJson);
          } catch (parseError) {
            // Fallback: extract rules directly
            try {
              // Try to extract rules array from structure
              const rulesMatch = cleanJson.match(/rules":\[(.*?)\]\}\}\}/);
              if (rulesMatch && rulesMatch[1]) {
                const rulesArray = JSON.parse(`[${rulesMatch[1]}]`);
                if (Array.isArray(rulesArray) && rulesArray.length > 0) {
                  discountData = { discount_rules: { data: { rules: rulesArray } } };
                }
              } else {
                // Last resort: extract rule values with regex
                const rules = this.extractRulesWithRegex(cleanJson);
                if (rules.length > 0) {
                  discountData = { discount_rules: { data: { rules } } };
                }
              }
            } catch (error) {
              console.error('Failed to extract rules', error);
            }
          }
          
          // If all parsing attempts fail with null, try example data
          if (cleanJson === 'null' && typeof discountJson === 'string' && 
              (discountJson.includes('spend_amount') || discountJson.includes('discount_rules'))) {
            console.log('Using example discount rules');
            discountData = {
              discount_rules: {
                data: {
                  rules: [
                    { spend_amount: 1500.5, discount_amount: 150.0, is_free_shipping: true },
                    { spend_amount: 750.25, discount_amount: 50.0, is_free_shipping: false },
                    { spend_amount: 2000.0, discount_amount: 300.0, is_free_shipping: true }
                  ]
                }
              }
            };
          }
        }
      }
      
      // Process the discount rules if we have valid data
      if (discountData) {
        this.processDiscountRules(discountData);
      }
    } catch (error) {
      console.error('Error parsing discount data:', error);
    }
  }
  
  // Extract rules with regex when JSON parsing fails
  extractRulesWithRegex(jsonString) {
    const rules = [];
    const spendAmountMatches = jsonString.match(/spend_amount[\":]+([\d\.]+)/g);
    const discountAmountMatches = jsonString.match(/discount_amount[\":]+([\d\.]+)/g);
    const isFreeShippingMatches = jsonString.match(/is_free_shipping[\":]+(\w+)/g);
    
    if (spendAmountMatches && discountAmountMatches) {
      for (let i = 0; i < spendAmountMatches.length; i++) {
        try {
          const spendMatch = spendAmountMatches[i].match(/([\d\.]+)/);
          const discountMatch = discountAmountMatches[i].match(/([\d\.]+)/);
          const freeShippingMatch = isFreeShippingMatches && isFreeShippingMatches[i] ? 
            isFreeShippingMatches[i].match(/(true|false)/) : null;
          
          if (spendMatch && discountMatch) {
            const spendAmount = parseFloat(spendMatch[1]);
            const discountAmount = parseFloat(discountMatch[1]);
            const isFreeShipping = freeShippingMatch ? freeShippingMatch[1] === 'true' : false;
            
            if (!isNaN(spendAmount) && !isNaN(discountAmount)) {
              rules.push({ spend_amount: spendAmount, discount_amount: discountAmount, is_free_shipping: isFreeShipping });
            }
          }
        } catch (error) {
          console.warn('Error extracting rule', error);
        }
      }
    }
    return rules;
  }
  
  // Process discount rules from parsed data
  processDiscountRules(discountData) {
    if (!discountData) return;
    
    // Extract rules from the structure
    if (discountData.discount_rules && 
        discountData.discount_rules.data && 
        discountData.discount_rules.data.rules) {
      
      this.discountRules = discountData.discount_rules.data.rules.sort((a, b) => a.spend_amount - b.spend_amount);
      this.freeShippingRules = this.discountRules.filter(rule => rule.is_free_shipping === true);
      this.discountOnlyRules = this.discountRules.filter(rule => !rule.is_free_shipping);
      
      // Set the lowest free shipping threshold as the main threshold
      if (this.freeShippingRules.length > 0) {
        const lowestFreeShippingRule = this.freeShippingRules.reduce(
          (lowest, rule) => rule.spend_amount < lowest.spend_amount ? rule : lowest,
          this.freeShippingRules[0]
        );
        this.threshold = lowestFreeShippingRule.spend_amount;
      } else if (this.discountRules.length > 0) {
        // If no free shipping rules, use the lowest discount rule
        this.threshold = this.discountRules[0].spend_amount;
      }
      
    } else {
      console.warn('Invalid discount data structure', discountData);
    }
  }
  
  connectedCallback() {
    this.initializeElements();
    this.registerEventListeners();
    this.isInitialized = true;
    
    // Parse discount data if available
    const discountData = this.getAttribute('data-discount');
    if (discountData) this.parseDiscountData(discountData);
    
    // Initial update
    this.updateProgressBar(this.cartTotal, true);
  }
  
  initializeElements() {
    this.progressBarFill = this.querySelector('#progress-bar-fill');
    this.milestoneElements = {
      combined: this.querySelector('#milestone-combined'),
      freeShipping: this.querySelector('#milestone-free-shipping'),
      discount: this.querySelector('#milestone-discount'),
      default: this.querySelector('#default-message')
    };
    this.amountLeftElement = this.querySelector('.amount-left');
    this.discountAmountElement = this.querySelector('.discount-amount');
    this.loadingIndicator = this.querySelector('.loading-indicator');
    this.container = this;
    
    if (!this.progressBarFill) {
      console.error('Required DOM elements not found in DrawerFreeShippingBar');
      return;
    }
    
    // Hide all milestone elements by default
    this.hideAllMilestones();
    
    // Get attribute values
    this.cartTotal = parseFloat(this.getAttribute('data-cart-total')) || 0;
    this.currentCartTotal = this.cartTotal;
    this.currency = this.getAttribute('data-currency') || '$';
    this.sectionId = this.getAttribute('data-section-id') || '';
    
    // Parse discount data
    const discountData = this.getAttribute('data-discount');
    if (discountData) this.parseDiscountData(discountData);
  }
  
  hideAllMilestones() {
    Object.values(this.milestoneElements).forEach(el => {
      if (el) el.style.display = 'none';
    });
  }
  
  showMilestone(milestoneName) {
    this.hideAllMilestones();
    const milestone = this.milestoneElements[milestoneName];
    if (milestone) milestone.style.display = 'block';
  }
  
  showLoading() {
    if (this.loadingIndicator) this.loadingIndicator.classList.add('visible');
  }
  
  hideLoading() {
    if (this.loadingIndicator) this.loadingIndicator.classList.remove('visible');
  }
  
  formatMoney(amount) {
    try {
      return `${this.currency}${Math.round(amount || 0)}`;
    } catch (error) {
      return `${this.currency}0`;
    }
  }
  
  registerEventListeners() {
    // Register for cart events with debouncing
    document.addEventListener('cart:updated', this.handleCartEvent.bind(this));
    document.addEventListener('cart:item-added', this.handleCartEvent.bind(this));
    document.addEventListener('cart:item-changed', this.handleCartEvent.bind(this));
    document.addEventListener('cart:cleared', this.handleCartEvent.bind(this));
    
    // Monitor form submissions
    document.addEventListener('submit', this.handleFormSubmit.bind(this));
    
    // Monitor clicks on cart actions
    document.addEventListener('click', this.handleCartActions.bind(this));
  }
  
  handleCartEvent(event) {
    this.showLoading();
    
    // Use a short timeout to debounce multiple events
    clearTimeout(this.updateTimeout);
    this.updateTimeout = setTimeout(() => this.updateAllShippingBars(), 300);
  }
  
  handleFormSubmit(event) {
      const form = event.target;
      if (form && (
          form.action.includes('/add') || 
          form.action.includes('/change') || 
          form.action.includes('/update') || 
          form.action.includes('/clear')
      )) {
        this.showLoading();
        setTimeout(() => this.updateAllShippingBars(), 500);
      }
  }
  
  handleCartActions(event) {
        const target = event.target;
        if (target && (
            target.closest('[data-cart-remove]') || 
            target.closest('[data-cart-update]') || 
            target.closest('[data-cart-clear]') ||
            target.closest('[href*="/cart/change"]') ||
            target.closest('[href*="/cart/clear"]')
        )) {
          this.showLoading();
          setTimeout(() => this.updateAllShippingBars(), 500);
    }
  }
  
  updateAllShippingBars() {
      this.fetchCartData()
        .then(cart => {
          if (cart && typeof cart.total_price === 'number') {
            const totalInDollars = cart.total_price / 100; // Convert cents to dollars
            
            // Update this shipping bar
            this.updateProgressBar(totalInDollars, true);
            
            // Find and update all other shipping bars
            const allShippingBars = document.querySelectorAll('drawer-free-shipping-bar');
              allShippingBars.forEach(bar => {
            if (bar !== this && typeof bar.setAttribute === 'function') {
                  bar.setAttribute('data-cart-total', totalInDollars);
                }
              });
          }
        })
        .catch(error => {
        console.error('Error updating shipping bars:', error);
          this.forceCartRefresh();
        });
  }
  
  fetchCartData() {
    this.showLoading();
    
    // Use Shopify's AJAX API to get cart data
    const cartUrl = window.Shopify && window.Shopify.routes ? 
                   `${window.Shopify.routes.root}cart.js` : 
                   '/cart.js';
    
    return fetch(cartUrl)
      .then(response => {
        if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);
        return response.json();
      })
      .then(cart => {
        if (!cart || typeof cart.total_price !== 'number') {
          throw new Error('Invalid cart data');
        }
        
        this.hideLoading();
        this.updateProgressBar(cart.total_price / 100); // Convert cents to dollars
        return cart;
      })
      .catch(error => {
        console.error('Error fetching cart data:', error);
        this.hideLoading();
        throw error;
      });
  }
  
  forceCartRefresh() {
    try {
      // Use Shopify's Section Rendering API
      const sectionId = this.sectionId || (this.closest('[data-id]')?.getAttribute('data-id'));
      
      if (sectionId) {
        const url = `${window.location.pathname}?section_id=${sectionId}`;
        this.showLoading();
        
        fetch(url)
          .then(response => response.text())
          .then(html => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            
            const newShippingBar = tempDiv.querySelector('drawer-free-shipping-bar');
            if (newShippingBar) {
              const newCartTotal = parseFloat(newShippingBar.getAttribute('data-cart-total')) || 0;
              this.cartTotal = newCartTotal;
              this.currentCartTotal = newCartTotal;
              this.updateProgressBar(newCartTotal);
            } else {
              this.fetchCartData();
            }
            this.hideLoading();
          })
          .catch(error => {
            console.error('Error using Section Rendering API:', error);
            this.fetchCartData();
          });
      } else {
        this.fetchCartData();
      }
    } catch (error) {
      console.error('Error in forceCartRefresh:', error);
      this.fetchCartData();
    }
  }
  
  updateProgressBar(newTotal = null, animate = true) {
    try {
      // Check initialization and do basic validation
      if (!this.isInitialized) {
        if (newTotal !== null && typeof newTotal === 'number' && !isNaN(newTotal)) {
          this.cartTotal = newTotal;
          this.currentCartTotal = newTotal;
        }
        return;
      }
      
      if (!this.progressBarFill) {
        console.warn('Progress bar not found - re-initializing elements');
        this.initializeElements();
        if (!this.progressBarFill) return;
      }
      
      // Validate total
      if (newTotal === null || typeof newTotal !== 'number' || isNaN(newTotal)) {
        newTotal = this.currentCartTotal || 0;
      }
      
      this.cartTotal = newTotal;
      this.currentCartTotal = newTotal;
      
      // Use the correct update method based on available data
      if (this.discountRules && this.discountRules.length > 0) {
        this.updateWithDiscountRules(newTotal, animate);
      } else {
        this.updateOriginalFreeShipping(newTotal, animate);
      }
    } catch (error) {
      console.error('Error updating progress bar:', error);
      this.hideLoading();
    }
  }
  
  updateWithDiscountRules(cartTotal, animate) {
    // Hide all milestones first
    this.hideAllMilestones();
    
    let progressPercentage = 0;
    let milestone = 'default';
    
    if (animate) this.showLoading();
    
    // Find the applicable rules
    const achievedRules = this.discountRules.filter(rule => cartTotal >= rule.spend_amount);
    const achievedFreeShipping = achievedRules.some(rule => rule.is_free_shipping);
    
    // Find next rule to aim for
    const nextRules = this.discountRules
      .filter(rule => cartTotal < rule.spend_amount)
      .sort((a, b) => a.spend_amount - b.spend_amount);
    const nextRule = nextRules.length > 0 ? nextRules[0] : null;
    
    // Find the highest discount among achieved rules
    let highestDiscount = 0;
    let highestDiscountRule = null;
    
    if (achievedRules.length > 0) {
      highestDiscountRule = achievedRules.reduce((highest, rule) => {
        return rule.discount_amount > highest.discount_amount ? rule : highest;
      }, achievedRules[0]);
      
      highestDiscount = highestDiscountRule.discount_amount;
    }
    
    // Determine which message to show based on current state
    if (achievedRules.length > 0) {
      // Get the highest threshold we've achieved
      const highestAchievedRule = achievedRules.reduce(
        (highest, rule) => rule.spend_amount > highest.spend_amount ? rule : highest,
        achievedRules[0]
      );
      
      // If we've achieved free shipping
      if (achievedFreeShipping) {
        // If there's a next discount milestone
        if (nextRule) {
          // This is the combined milestone case - exactly matching the new image
          milestone = 'combined';
          
          const amountNeeded = nextRule.spend_amount - cartTotal;
          
          if (this.milestoneElements.combined) {
            // Find elements within the combined milestone
            const amountLeftEl = this.milestoneElements.combined.querySelector('.amount-left');
            const discountAmountEl = this.milestoneElements.combined.querySelector('.discount-amount');
            
            // Update text content with real values
            if (amountLeftEl) amountLeftEl.textContent = this.formatMoney(amountNeeded);
            
            // Use the actual discount amount from the rule
            if (discountAmountEl && nextRule.discount_amount > 0) {
              // Get direct percent value from rule
              discountAmountEl.textContent = `${nextRule.discount_amount}% off`;
            }
            
            // Update first line to show current discount if applicable
            const firstLineEl = this.milestoneElements.combined.querySelector('.milestone-line:first-child');
            if (firstLineEl && highestDiscount > 0) {
              firstLineEl.innerHTML = 
                `Congratulations, you've unlocked <span class="free-amount">free shipping!</span> and <span class="free-amount">${highestDiscount}% off!</span>`;
            }
          }
          
          // Calculate progress toward the next rule
          const range = nextRule.spend_amount - highestAchievedRule.spend_amount;
          const progress = cartTotal - highestAchievedRule.spend_amount;
          progressPercentage = Math.min(100, Math.round((progress / range) * 100));
        } else {
          // We've achieved the highest milestone - show free shipping
          milestone = 'freeShipping';
          
          // Show the highest discount achieved
          if (highestDiscount > 0) {
            if (this.milestoneElements.freeShipping) {
              this.milestoneElements.freeShipping.innerHTML = 
                `Congratulations, you've unlocked <span class="free-amount-and-discount">free shipping</span>` +
                `<span class="free-amount-and-discount"> <br>  and ${highestDiscount}% off!</span>`;
            }
          }
          
          progressPercentage = 100;
        }
      } else if (nextRule) {
        // We've achieved some discount but not free shipping yet
        milestone = 'discount';
        
        const amountNeeded = nextRule.spend_amount - cartTotal;
        
        // Determine if the next rule offers free shipping
        if (nextRule.is_free_shipping) {
          if (this.milestoneElements.discount) {
            // Show current discount if applicable
            if (highestDiscount > 0) {
              this.milestoneElements.discount.innerHTML = 
                `You've unlocked <span class="discount-amount">${highestDiscount}% off!</span><br>` +
                `You're just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from ` +
                `<span class="free-amount">free shipping!</span>`;
            } else {
              this.milestoneElements.discount.innerHTML = 
                `You're just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from ` +
                `<span class="free-amount">free shipping!</span>`;
            }
          }
        } else {
          // Use direct discount value from rule
          const nextDiscountAmount = nextRule.discount_amount;
          
          if (this.milestoneElements.discount) {
            // Show current discount if applicable
            if (highestDiscount > 0) {
              this.milestoneElements.discount.innerHTML = 
                `You've unlocked <span class="discount-amount">${highestDiscount}% off!</span><br>` +
                `You're just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from ` +
                `<span class="discount-amount">${nextDiscountAmount}% off!</span>`;
            } else {
              this.milestoneElements.discount.innerHTML = 
                `You're just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from ` +
                `<span class="discount-amount">${nextDiscountAmount}% off!</span>`;
            }
          }
        }
        
        // Calculate progress
        const range = nextRule.spend_amount - highestAchievedRule.spend_amount;
        const progress = cartTotal - highestAchievedRule.spend_amount;
        progressPercentage = Math.min(100, Math.round((progress / range) * 100));
      } else {
        // We've achieved the highest rule but it doesn't include free shipping
        milestone = 'discount';
        
        // Use direct discount value from rule
        if (this.milestoneElements.discount) {
          this.milestoneElements.discount.innerHTML = 
            `Congratulations, you've unlocked <span class="discount-amount">${highestDiscount}% off!</span>`;
        }
        
        progressPercentage = 100;
      }
    } else if (nextRule) {
      // We haven't achieved any rules yet - show progress to the first milestone
      milestone = 'default';
      
      const amountNeeded = nextRule.spend_amount - cartTotal;
      
      // Determine if the first rule offers free shipping
      if (nextRule.is_free_shipping) {
        if (this.milestoneElements.default) {
          this.milestoneElements.default.innerHTML = 
            `Just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from ` +
            `<span class="free-amount">free shipping!</span>`;
        }
      } else {
        // Use direct discount value from rule
        const discountAmount = nextRule.discount_amount;
        
        if (this.milestoneElements.default) {
          this.milestoneElements.default.innerHTML = 
            `Just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from ` +
            `<span class="discount-amount">${discountAmount}% off!</span>`;
        }
      }
      
      // Calculate progress to the first rule
      progressPercentage = Math.min(100, Math.round((cartTotal / nextRule.spend_amount) * 100));
    } else {
      // No rules configured
      milestone = 'default';
      
      if (this.milestoneElements.default) {
        this.milestoneElements.default.innerHTML = 
          `Add items to your cart to qualify for free shipping and discounts.`;
      }
    }
    
    // Show the appropriate milestone
    this.showMilestone(milestone);
    
    // Update the progress bar color based on the milestone
    this.updateProgressBarColors(milestone);
    
    // Update progress bar with animation if needed
    this.updateProgressBarUI(progressPercentage, animate);
  }
  
  updateProgressBarColors(milestone) {
    // Add milestone-specific class to progress bar for proper styling
    if (this.progressBarFill) {
      this.progressBarFill.classList.remove('free-shipping-achieved', 'discount-progress');
      
      if (milestone === 'combined' || milestone === 'freeShipping') {
        this.progressBarFill.classList.add('free-shipping-achieved');
      } else if (milestone === 'discount') {
        this.progressBarFill.classList.add('discount-progress');
      }
    }
  }
  
  updateOriginalFreeShipping(newTotal, animate) {
    // If there's no threshold set yet, we can't show anything meaningful
    if (!this.threshold) {
      console.warn('No threshold set for free shipping bar');
      return;
    }
    
    // Calculate progress percentage
    const progressPercentage = Math.min(100, Math.round((newTotal / this.threshold) * 100));
    
    // Hide all milestones first
    this.hideAllMilestones();
    
    // Check if we've reached the threshold
    const amountLeft = Math.max(0, this.threshold - newTotal);
    const freeShippingAchieved = amountLeft <= 0;
    
    // Update messages based on current state
    if (freeShippingAchieved) {
      if (this.milestoneElements.freeShipping) {
        this.milestoneElements.freeShipping.innerHTML = 
          `Congratulations, you've unlocked <span class="free-amount">free shipping!</span>`;
        this.showMilestone('freeShipping');
        
        // Update progress bar color for free shipping
        if (this.progressBarFill) {
          this.progressBarFill.classList.add('free-shipping-achieved');
          this.progressBarFill.classList.remove('discount-progress');
        }
      }
    } else {
      // Format the amount left
      const amountLeftFormatted = this.formatMoney(amountLeft);
      
      if (this.milestoneElements.default) {
        this.milestoneElements.default.innerHTML = 
          `Just <span class="amount-left">${amountLeftFormatted}</span> away from ` +
          `<span class="free-amount">free shipping!</span>`;
        this.showMilestone('default');
        
        // Reset progress bar color
        if (this.progressBarFill) {
          this.progressBarFill.classList.remove('free-shipping-achieved', 'discount-progress');
        }
      }
    }
    
    // Update progress bar with animation if needed
    this.updateProgressBarUI(progressPercentage, animate);
  }
  
  updateProgressBarUI(progressPercentage, animate) {
    if (animate) {
      this.showLoading();
      
      // Animate the progress bar
      setTimeout(() => {
        if (this.progressBarFill) {
          this.progressBarFill.style.transition = 'width 1s ease-in-out';
          this.progressBarFill.style.width = `${progressPercentage}%`;
        }
        
        // Remove loading state after animation completes
        setTimeout(() => this.hideLoading(), 1000);
      }, 100);
    } else {
      // Immediate update without animation
      if (this.progressBarFill) {
        this.progressBarFill.style.width = `${progressPercentage}%`;
      }
      this.hideLoading();
    }
  }

  updateElement(params = {}) {
    const {
      cartInstance,
      discountRules,
      combinedMilestone
    } = params;

    if (!discountRules || !cartInstance) return;

    if (combinedMilestone) {
      const milestoneHTML = document.querySelector(`.${this.combinedMilestoneClass}`);
      if (milestoneHTML) {
        // Check if we have a shipping icon in the milestone HTML
        const hasShippingIcon = milestoneHTML.querySelector('.shipping-icon');
        const hasFreeShippingText = milestoneHTML.querySelector('.shipping-text');

        if (hasShippingIcon && hasFreeShippingText) {
          // Update the progress bar with the combined milestone which includes shipping icon
          const progress = this._getProgressForCombinedMilestone(discountRules, cartInstance);
          const milestoneBubble = document.querySelector(`.${this.progressClass}`);
          if (milestoneBubble) {
            milestoneBubble.style.left = `${progress}%`;
          }
          // Show/hide shipping icon based on whether free shipping is achieved
          const isFreeShippingAchieved = this._isFreeShippingAchieved(discountRules, cartInstance);
          hasShippingIcon.classList.toggle('free-shipping-achieved', isFreeShippingAchieved);
          hasFreeShippingText.classList.toggle('free-shipping-achieved', isFreeShippingAchieved);
        } else {
          // Fallback to original behavior if new structure is not found
          const progress = this._getProgressForCombinedMilestone(discountRules, cartInstance);
          const milestoneBubble = document.querySelector(`.${this.progressClass}`);
          if (milestoneBubble) {
            milestoneBubble.style.left = `${progress}%`;
          }
        }
      }
    } else {
      // Original behavior for non-combined milestone
      const milestoneHTML = document.querySelector(`.${this.milestoneClass}`);
      const percentage = this._getProgressPercentage(discountRules, cartInstance);
      if (milestoneHTML) milestoneHTML.innerHTML = this._getMilestoneText(discountRules, cartInstance);
      
      const progressBar = document.querySelector(`.${this.progressBarClass}`);
      if (progressBar) progressBar.style.width = `${percentage}%`;
    }

    // Update text elements regardless of milestone type
    const remainingAmount = this._getRemainingAmountText(discountRules, cartInstance);
    const remainingAmountEls = document.querySelectorAll(`.${this.remainingAmountClass}`);
    if (remainingAmountEls) {
      remainingAmountEls.forEach(el => {
        el.innerHTML = remainingAmount;
      });
    }

    const milestoneReached = this._hasMilestoneBeenReached(discountRules, cartInstance);
    const currentAmountEls = document.querySelectorAll(`.${this.currentAmountClass}`);
    if (currentAmountEls) {
      currentAmountEls.forEach(el => {
        el.innerHTML = this._getCurrentAmountText(discountRules, cartInstance);
      });
    }

    document.querySelectorAll(`.${this.drawerClass}`).forEach(drawerEl => {
      drawerEl.classList.toggle(this.milestoneReachedClass, milestoneReached);
    });
  }

  /**
   * Helper method to check if free shipping has been achieved
   * @param {Array} discountRules - The discount rules
   * @param {Object} cartInstance - The cart instance
   * @returns {Boolean} - Whether free shipping has been achieved
   * @private
   */
  _isFreeShippingAchieved(discountRules, cartInstance) {
    if (!discountRules || !cartInstance) return false;

    const currentCartTotal = cartInstance.total_price / 100;
    const freeShippingRules = discountRules.filter(rule => rule.type === 'free_shipping');

    if (freeShippingRules.length === 0) return false;

    // Sort rules by threshold to find the highest applicable threshold
    const sortedRules = [...freeShippingRules].sort((a, b) => a.threshold - b.threshold);
    
    // Check if current cart total meets or exceeds any free shipping threshold
    return sortedRules.some(rule => currentCartTotal >= rule.threshold);
  }

  // Define missing class properties and methods for updateElement
  combinedMilestoneClass = 'drawer-free-shipping-bar__headline milestone-combined';
  milestoneClass = 'drawer-free-shipping-bar__headline';
  progressClass = 'drawer-free-shipping-bar__progress-bubble';
  progressBarClass = 'drawer-free-shipping-bar__progress-bar-fill';
  remainingAmountClass = 'amount-left';
  currentAmountClass = 'current-amount';
  drawerClass = 'drawer-free-shipping-bar';
  milestoneReachedClass = 'milestone-reached';
  
  _getProgressForCombinedMilestone(discountRules, cartInstance) {
    if (!discountRules || !cartInstance) return 0;
    const currentCartTotal = cartInstance.total_price / 100;
    
    // Find the highest threshold that has been met
    const achievedRules = discountRules.filter(rule => currentCartTotal >= rule.spend_amount);
    
    if (achievedRules.length === 0) {
      // No threshold met yet
      const lowestRule = discountRules.sort((a, b) => a.spend_amount - b.spend_amount)[0];
      return Math.min(100, Math.round((currentCartTotal / lowestRule.spend_amount) * 100));
    }
    
    // Get highest achieved rule
    const highestAchievedRule = achievedRules.reduce(
      (highest, rule) => rule.spend_amount > highest.spend_amount ? rule : highest,
      achievedRules[0]
    );
    
    // Find next rule if any
    const nextRules = discountRules
      .filter(rule => currentCartTotal < rule.spend_amount)
      .sort((a, b) => a.spend_amount - b.spend_amount);
    
    if (nextRules.length === 0) {
      // Reached highest threshold
      return 100;
    }
    
    const nextRule = nextRules[0];
    const range = nextRule.spend_amount - highestAchievedRule.spend_amount;
    const progress = currentCartTotal - highestAchievedRule.spend_amount;
    
    return Math.min(100, Math.max(0, Math.round(
      ((currentCartTotal - highestAchievedRule.spend_amount) / 
      (nextRule.spend_amount - highestAchievedRule.spend_amount)) * 100
    )));
  }
  
  _getProgressPercentage(discountRules, cartInstance) {
    if (!discountRules || !cartInstance) return 0;
    
    const currentCartTotal = cartInstance.total_price / 100;
    
    // Get the next threshold to reach
    const nextThresholds = discountRules
      .filter(rule => currentCartTotal < rule.spend_amount)
      .sort((a, b) => a.spend_amount - b.spend_amount);
    
    if (nextThresholds.length === 0) {
      // All thresholds have been reached
      return 100;
    }
    
    const nextThreshold = nextThresholds[0].spend_amount;
    
    // Get the previous threshold that was reached
    const previousThresholds = discountRules
      .filter(rule => currentCartTotal >= rule.spend_amount)
      .sort((a, b) => b.spend_amount - a.spend_amount);
    
    const prevThreshold = previousThresholds.length > 0 ? 
      previousThresholds[0].spend_amount : 0;
    
    // Calculate progress between prev and next thresholds
    const range = nextThreshold - prevThreshold;
    const progress = currentCartTotal - prevThreshold;
    
    return Math.min(100, Math.round((progress / range) * 100));
  }
  
  _getMilestoneText(discountRules, cartInstance) {
    if (!discountRules || !cartInstance) return '';
    
    const currentCartTotal = cartInstance.total_price / 100;
    
    // Get achieved rules
    const achievedRules = discountRules
      .filter(rule => currentCartTotal >= rule.spend_amount)
      .sort((a, b) => b.spend_amount - a.spend_amount);
    
    // Find the highest achieved discount
    let highestDiscount = 0;
    let highestDiscountRule = null;
    
    if (achievedRules.length > 0) {
      highestDiscountRule = achievedRules.reduce((highest, rule) => {
        return rule.discount_amount > highest.discount_amount ? rule : highest;
      }, achievedRules[0]);
      
      highestDiscount = highestDiscountRule.discount_amount;
    }
    
    const achievedFreeShipping = achievedRules.some(rule => rule.is_free_shipping);
    
    // Get the next threshold to reach
    const nextRules = discountRules
      .filter(rule => currentCartTotal < rule.spend_amount)
      .sort((a, b) => a.spend_amount - b.spend_amount);
    
    if (nextRules.length === 0) {
      // All thresholds reached
      if (achievedFreeShipping && highestDiscount > 0) {
        return `Congratulations, you've unlocked <span class="free-amount">free shipping!</span> and <span class="discount-amount">${highestDiscount}% off!</span>`;
      } else if (achievedFreeShipping) {
        return `Congratulations, you've unlocked <span class="free-amount">free shipping!</span>`;
      } else if (highestDiscount > 0) {
        return `Congratulations, you've unlocked <span class="discount-amount">${highestDiscount}% off!</span>`;
      }
    } else {
      // Still have thresholds to reach
      const nextRule = nextRules[0];
      const amountNeeded = nextRule.spend_amount - currentCartTotal;
      
      if (achievedFreeShipping && nextRule.discount_amount > 0) {
        // We have free shipping but can get a higher discount
        const nextDiscountAmount = nextRule.discount_amount;
        
        // If we also have a current discount, show that too
        if (highestDiscount > 0) {
          return `You've unlocked <span class="free-amount">free shipping!</span> and <span class="discount-amount">${highestDiscount}% off!</span><br>` +
                 `You're just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from <span class="discount-amount">${nextDiscountAmount}% off!</span>`;
        }
        
        return `You've unlocked <span class="free-amount">free shipping!</span><br>` +
               `You're just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from <span class="discount-amount">${nextDiscountAmount}% off!</span>`;
      } else if (highestDiscount > 0 && nextRule.is_free_shipping) {
        // We have a discount but can get free shipping
        return `You've unlocked <span class="discount-amount">${highestDiscount}% off!</span><br>` +
               `You're just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from <span class="free-amount">free shipping!</span>`;
      } else if (nextRule.is_free_shipping) {
        // We can get free shipping
        return `You're just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from <span class="free-amount">free shipping!</span>`;
      } else if (nextRule.discount_amount > 0) {
        // We can get a discount
        const nextDiscountAmount = nextRule.discount_amount;
        
        // If we already have a discount, show that too
        if (highestDiscount > 0) {
          return `You've unlocked <span class="discount-amount">${highestDiscount}% off!</span><br>` +
                 `You're just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from <span class="discount-amount">${nextDiscountAmount}% off!</span>`;
        }
        
        return `You're just <span class="amount-left">${this.formatMoney(amountNeeded)}</span> away from <span class="discount-amount">${nextDiscountAmount}% off!</span>`;
      }
    }
    
    return `Add items to your cart to qualify for free shipping and discounts.`;
  }
  
  _getRemainingAmountText(discountRules, cartInstance) {
    if (!discountRules || !cartInstance) return '';
    
    const currentCartTotal = cartInstance.total_price / 100;
    
    // Get the next threshold to reach
    const nextRules = discountRules
      .filter(rule => currentCartTotal < rule.spend_amount)
      .sort((a, b) => a.spend_amount - b.spend_amount);
    
    if (nextRules.length === 0) {
      // All thresholds reached
      return '';
    }
    
    const nextRule = nextRules[0];
    const amountNeeded = nextRule.spend_amount - currentCartTotal;
    
    return this.formatMoney(amountNeeded);
  }
  
  _getCurrentAmountText(discountRules, cartInstance) {
    if (!discountRules || !cartInstance) return '';
    return this.formatMoney(cartInstance.total_price / 100);
  }
  
  _hasMilestoneBeenReached(discountRules, cartInstance) {
    if (!discountRules || !cartInstance) return false;
    
    const currentCartTotal = cartInstance.total_price / 100;
    return discountRules.some(rule => currentCartTotal >= rule.spend_amount);
  }
}

// Register the custom element
customElements.define('drawer-free-shipping-bar', DrawerFreeShippingBar);

// Demo functionality (for development only)
document.addEventListener('DOMContentLoaded', () => {
  const demoMode = false; // Set to true to enable demo
  
  if (demoMode) {
    const shippingBar = document.querySelector('drawer-free-shipping-bar');
    if (!shippingBar) return;
    
    let currentProgress = 0;
    
    function simulateCartUpdate() {
      currentProgress += 10;
      if (currentProgress > 100) currentProgress = 0;
      
      const threshold = parseFloat(shippingBar.getAttribute('data-free-shipping-threshold')) || 350;
      const newCartTotal = (currentProgress / 100) * threshold;
      
      // Simulate different cart actions
      if (currentProgress === 30) {
        // Simulate adding a $2 item
        shippingBar.handleCartAction('add', 200);
      } else if (currentProgress === 60) {
        // Simulate changing an item, adding $3 to total
        shippingBar.handleCartAction('update', 300);
      } else if (currentProgress === 90) {
        // Simulate clearing cart
        shippingBar.handleCartAction('remove', 1000);
      } else {
        shippingBar.updateProgressBar(newCartTotal);
      }
    }
    
    // Update every 3 seconds
    setInterval(simulateCartUpdate, 3000);
  }
});
